import { observable, computed, action, transaction } from 'mobx'
import { inspector } from '../shared/decorators/inspector'
import Model from '../shared/model'
import { Store } from '../shared/store'
import { apiClient } from '../shared/api'
import { store as uiStore } from '../ui'
import { deepGet } from '../shared/obj'
import * as TemplateLoader from '../shared/template/loader'
import * as config from '../config'
import { addToCollection } from '../shared/utils'

class Template extends Model {
  // Put all template properties here.
  @observable name = '';

  @observable module = null;

  @observable shortcut = null;

  @observable templateType = 'article';

  get moduleLoaded() {
    return this.module
  }

  set(data, opts = {}) {
    super.set(data, opts)
    if (opts.action === 'load') {
      this.synced = true
    }
    this.name
      = deepGet(data, `i18n.${uiStore.language}.name`)
      || deepGet(data, 'i18n.de.name')
      || ''
  }

  @computed get asJSON() {
    return {
      id: this.id,
      name: this.name,
    }
  }

  resetModule() {
    transaction(() => {
      this.shortcut = null
      this.module = null
      this.moduleFile = null
    })
  }

  /**
   * Seems to be unused
   * @deprecated
   */
  initModule(module, moduleFile) {
    transaction(() => {
      this.module = module
      this.moduleFile = moduleFile
    })
  }
}

@apiClient
@inspector('loadTemplateModule', 'template.loading')
class TemplateStore extends Store {
  // Put all template list properties here. This regargds also state properties.
  @observable collection = [];

  @observable didCompleteLoad = false;

  @observable current = {};
  //  @observable currentState = observable(asMap()) - mobx update - delete when no problems occur

  @observable alwaysReloadStyleSheets = false; // !config.isProduction || false

  @observable loading = false;

  // Put all properties that are not to be observed here:

  constructor(opts = {}) {
    super(opts)

    this.Model = Template
  }

  load(id, opts = {}) {
    if (id) {
      return this.dispatch('get', {
        path: `template/${id}`
      }).then((response) => {
        if (!response || !response.body || response.error) {
          throw new Error('error in page get')
        }
        return addToCollection(this, this.collection, response.body.data, Template)
      })
    }
    // if no id, the load all
    return this.dispatch('get', {
      path: 'template'
    }).then((response) => {
      if (!response || !response.body || response.error) {
        throw new Error('error in page get')
      }
      response.body.data.forEach((template) => {
        addToCollection(this, this.collection, template, Template)
      })
      this.didCompleteLoad = true
      return true
    })

  }

  openTemplateModuleByShortcut(shortcut) {
    this.loadTemplateModule(shortcut)
  }

  loadTemplateModule(shortcut) {
    const templateLoader = TemplateLoader.getInstance(
      this.collection,
      uiStore.language,
      config.articleTemplateBasePath
    )

    return templateLoader.loadTemplateByShortcut(shortcut, config)
  }

  getLoadedTemplateById(id) {
    const template = this.getById(id)

    if (template && template.module && template.moduleLoaded) {
      return Promise.resolve(template)
    }

    let promise = Promise.resolve(template)

    if (!(template && template.module)) {
      promise = this.load(id, { force: true })
    }

    return promise
      .then((model) => {
        if (!model) {
          // eslint-disable-next-line
          throw new Error(
            `The Template module file with the id \`${id}\` (${template.name}) could not be loaded`
          )
        }
        return model
      })
      .then(tmpl => this.loadTemplateModule(tmpl.shortcut))
  }

  @action
  actionResetModule(id) {
    const template = id !== undefined ? this.getById(id) : this.current

    if (template) {
      const templateLoader = TemplateLoader.getInstance(
        this.collection,
        uiStore.language,
        config.articleTemplateBasePath
      )
      templateLoader.removeTemplateByShortcut(template.shortcut)

      template.resetModule()
    }
  }
}

export const templateStore = new TemplateStore()
